import * as THREE from 'three';
import { CAMERA } from '../core/SimConfig.js';

export const CameraMode = { CHASE: 0, COCKPIT: 1, ENEMY: 2, START: 3, DESTROYED: 4, ACTION: 5, INTRO: 6 };

/** True while the player's own A-10 is the camera subject and he's flying it —
 *  chase, cockpit, or an action-camera mount. Not the enemy orbit, the
 *  start-screen beauty shot, or the wreck view. */
export function isFlightMode(mode) {
  return mode === CameraMode.CHASE || mode === CameraMode.COCKPIT || mode === CameraMode.ACTION;
}

// Which in-cockpit models a view draws. `interior` swaps the HD cockpit in for
// the SD exterior shell; `pilot` draws the seated pilot (a top-level node since
// the action cameras need to frame him without the shell). Every mode but
// ACTION resolves to one of these two shared objects — an action shot carries
// its own, built from its node name — so the per-frame visibility gates in App
// allocate nothing.
const DRESS_EXTERIOR = Object.freeze({ interior: false, pilot: true });
const DRESS_COCKPIT = Object.freeze({ interior: true, pilot: false });

export default class CameraManager {
  constructor({ camera, chaseCamera, cockpitCamera, enemyCamera, startCamera = null, actionCamera = null, controller, initialMode = CameraMode.CHASE }) {
    this.camera = camera;
    this.chaseCamera = chaseCamera;
    this.cockpitCamera = cockpitCamera;
    this.enemyCamera = enemyCamera;
    this.startCamera = startCamera;
    this.actionCamera = actionCamera;
    // First-run intro shots (see `playIntro`) — attached only for that run.
    this.introCamera = null;
    this.controller = controller;

    this.mode = initialMode;
    this.transitionDuration = 0.4; // seconds — default for camera-mode toggles

    // Transition state
    this._transitioning = false;
    this._transitionTime = 0;
    this._transitionDuration = this.transitionDuration; // active duration (overridable per-transition)
    this._fromPos = new THREE.Vector3();
    this._fromQuat = new THREE.Quaternion();
    this._fromFov = this._fovForMode(initialMode);
    this._toMode = initialMode;
    // Start pose re-based onto the airframe while both ends of a transition
    // ride it (see `_anchorFrom`).
    this._fromAnchored = false;
    this._fromOffset = new THREE.Vector3();

    // Cockpit/pilot model state, resolved per end of a transition rather than
    // per frame (see `_dressing`).
    this._dressing = this._dressingFor(initialMode);
    this._fromDressing = this._dressing;
    this._toDressing = this._dressing;

    // Track the last A-10 mode so X toggles back to it
    this._previousMode = CameraMode.CHASE;

    // The start-screen orbit camera owns its own mouse listeners — only live
    // while it's the active mode.
    if (initialMode === CameraMode.START) this.startCamera?.enable();

    // Initialize FOV
    this.camera.fov = this._fovForMode(initialMode);
    this.camera.updateProjectionMatrix();
  }

  get active() {
    if (this.mode === CameraMode.ENEMY) return this.enemyCamera;
    if (this.mode === CameraMode.START) return this.startCamera;
    if (this.mode === CameraMode.ACTION) return this.actionCamera;
    if (this.mode === CameraMode.INTRO) return this.introCamera;
    // DESTROYED reuses the chase camera (pulled out to its destroyed distance).
    if (this.mode === CameraMode.DESTROYED) return this.chaseCamera;
    return this.mode === CameraMode.CHASE ? this.chaseCamera : this.cockpitCamera;
  }

  get target() {
    if (this._toMode === CameraMode.ENEMY) return this.enemyCamera;
    if (this._toMode === CameraMode.START) return this.startCamera;
    if (this._toMode === CameraMode.ACTION) return this.actionCamera;
    if (this._toMode === CameraMode.INTRO) return this.introCamera;
    if (this._toMode === CameraMode.DESTROYED) return this.chaseCamera;
    return this._toMode === CameraMode.CHASE ? this.chaseCamera : this.cockpitCamera;
  }

  /**
   * Which in-cockpit models the current view wants drawn — the HD interior vs
   * the SD exterior shell, and the seated pilot. Resolves to one of two shared
   * objects (or the active action shot's own), so reading it costs nothing.
   *
   * Outside a transition this is just the active mode's dressing. During one it
   * picks an *end* to follow, and when to change over:
   *
   * - The ends disagree about the interior (entering or leaving the cockpit):
   *   change over when the camera comes within `CAMERA.cockpit.lodSwapRadius`
   *   of the pilot's eye point — i.e. roughly as it passes through the canopy.
   *   Swapping on the frame the transition starts puts the pop in open air
   *   where the whole airframe is in frame; swapping at the crossing hides it
   *   behind the structure the camera is threading, and costs nothing, since
   *   neither model is more than a metre or two from the camera at that moment.
   *   Distance rather than a progress fraction so it holds for every entry —
   *   the 0.4 s chase toggle, the enemy view, and the long cinematic glide from
   *   the start screen all travel different distances over different durations.
   * - They agree on the interior but not on the pilot (one cockpit action
   *   mount to another): change over at the halfway point, where the camera is
   *   moving fastest and a model appearing is least likely to be caught. There
   *   is no crossing to hide behind — both ends are inside the canopy.
   */
  get dressing() {
    const from = this._transitioning ? this._fromDressing : this._dressing;
    if (!this._transitioning) return from;
    const to = this._toDressing;
    if (from === to) return from;
    if (from.interior !== to.interior) {
      const r = CAMERA.cockpit.lodSwapRadius;
      const inside = this.camera.position.distanceToSquared(this.cockpitCamera.position) <= r * r;
      return from.interior === inside ? from : to;
    }
    return this._transitionTime >= this._transitionDuration * 0.5 ? to : from;
  }

  /** Whether the HD cockpit interior should be drawn in place of the SD shell. */
  get cockpitInterior() {
    return this.dressing.interior;
  }

  /** Whether the seated pilot should be drawn. */
  get pilotVisible() {
    return this.dressing.pilot;
  }

  /**
   * True while the player's viewpoint is *inside* the cockpit — the cockpit
   * view, or an action mount tagged `_cockpit`. Unlike `cockpitInterior` this
   * resolves to the destination as soon as a transition starts, which is what
   * the cockpit systems want: the avionics, the instrument springs, the TV
   * render and the engine-audio crossfade all need the lead time to ramp or
   * power up, rather than snapping on the frame the camera arrives.
   */
  get cockpitView() {
    return (this._transitioning ? this._toDressing : this._dressing).interior;
  }

  /**
   * True while the viewpoint rides the pilot's head — every mode but an action
   * mount, which is a lens bolted to the airframe and does not move with him.
   * Resolves to the destination as a transition starts, as `cockpitView` does;
   * what reads it (the HUD's collimation parallax) eases rather than snaps.
   */
  get pilotEyeView() {
    return (this._transitioning ? this._toMode : this.mode) !== CameraMode.ACTION;
  }

  /**
   * 0..1 — how far the view is into the cockpit, eased along the active
   * transition with the same curve App's shadow frustum used to derive itself.
   * Drives the shadow-frustum tightening, which wants a continuous ramp rather
   * than the model swap's single crossing.
   */
  get interiorBlend() {
    const from = (this._transitioning ? this._fromDressing : this._dressing).interior ? 1 : 0;
    if (!this._transitioning) return from;
    const to = this._toDressing.interior ? 1 : 0;
    if (from === to) return from;
    let t = Math.min(this._transitionTime / this._transitionDuration, 1);
    t = t * t * (3 - 2 * t);
    return from + (to - from) * t;
  }

  /** The models a given mode draws. Action mounts carry their own, read off the
   *  node name by ActionCamera; everything else is interior-or-not. */
  _dressingFor(mode) {
    if (mode === CameraMode.ACTION) return this.actionCamera?.shot?.dressing || DRESS_EXTERIOR;
    return mode === CameraMode.COCKPIT ? DRESS_COCKPIT : DRESS_EXTERIOR;
  }

  _fovForMode(mode) {
    if (mode === CameraMode.ENEMY) return CAMERA.enemy.fov;
    if (mode === CameraMode.START) return CAMERA.start.fov;
    if (mode === CameraMode.INTRO) return this.introCamera?.fovFor(this.camera.aspect) ?? CAMERA.start.fov;
    // An action mount's lens is whatever was authored in Blender, re-fitted to
    // the live window. Deliberately not zoomable: it's a fixed lens, and RMB is
    // still doing its other jobs (Maverick slew, gunnery gains) from here.
    if (mode === CameraMode.ACTION) return this.actionCamera?.fovFor(this.camera.aspect) ?? CAMERA.action.fallbackFov;
    // The external views share one zoom: RMB narrows the FOV while ChaseCamera
    // pulls the follow distance back to match (see CAMERA.chase.zoomDistanceScale).
    if (mode === CameraMode.DESTROYED) {
      return this.controller?.zooming ? CAMERA.chase.zoomFov : CAMERA.chase.fov;
    }
    if (mode === CameraMode.CHASE && this.controller?.zooming) return CAMERA.chase.zoomFov;
    // "Aim Maverick" mode narrows the cockpit view to focus on the TV (checked
    // before the RMB zoom, since RMB is repurposed for aiming in this mode).
    if (mode === CameraMode.COCKPIT && this.controller?.maverickAiming) return CAMERA.cockpit.maverickAimFov;
    if (mode === CameraMode.COCKPIT && this.controller?.zooming) return CAMERA.cockpit.zoomFov;
    return mode === CameraMode.CHASE ? CAMERA.chase.fov : CAMERA.cockpit.fov;
  }

  /** Modes whose pose rides the player's own airframe — chase, cockpit, an
   *  action mount, and the wreck view (the chase camera pulled back). */
  _ridesAircraft(mode) {
    return isFlightMode(mode) || mode === CameraMode.DESTROYED;
  }

  /**
   * Re-base the captured start pose onto the airframe when both ends of the
   * transition ride it.
   *
   * `_fromPos` is a world point, but the destination is re-read every frame and
   * travels with the jet — so lerping between them pins the camera to a spot
   * the aircraft has already left, which in the aircraft's frame is a slide
   * *backward* along the velocity vector. It peaks at ~0.27·V·T (≈20 m at
   * cruise), and the eased curve spends its whole slow-in there, which is why
   * it reads as a lurch rather than as slack. Storing the offset from the
   * aircraft origin instead, and rebuilding `_fromPos` from the live position
   * each frame, moves the glide into the frame both endpoints share — so a
   * cockpit-to-cockpit-mount move is the few inches it actually is instead of a
   * 20 m excursion out past the tail and back.
   *
   * The offset is world-axis, not body-local: carrying the aircraft's rotation
   * too would be the strict rigid-body answer, but it swings a 15 m chase
   * anchor through a wide arc if the player rolls mid-transition, and the
   * translation is the whole of the artifact.
   *
   * A transition with either end anchored to something else — the enemy orbit,
   * the start-screen glide — stays in world space, where its travel is real.
   */
  _anchorFrom(from, to) {
    const aircraft = this.chaseCamera?.aircraft;
    this._fromAnchored = !!aircraft && this._ridesAircraft(from) && this._ridesAircraft(to);
    if (this._fromAnchored) this._fromOffset.copy(this._fromPos).sub(aircraft.position);
  }

  _startTransition(next, duration = this.transitionDuration) {
    // A destroyed A-10 has no intact cockpit to sit in — redirect any request
    // to enter cockpit (Enter toggle, or restoring from the enemy view) to the
    // external wreck-watching view so the player stays outside the airframe.
    if (next === CameraMode.COCKPIT && this.chaseCamera.destroyed) next = CameraMode.DESTROYED;
    this._fromPos.copy(this.camera.position);
    this._fromQuat.copy(this.camera.quaternion);
    this._fromFov = this.camera.fov;
    // Mid-glide the camera belongs to the frame it was heading for rather than
    // the one it nominally left — the same reasoning as the live dressing read.
    this._anchorFrom(this._transitioning ? this._toMode : this.mode, next);
    // Read the live dressing before re-pointing the transition: mid-glide it's
    // whichever end is currently drawn, not the mode we nominally came from.
    this._fromDressing = this.dressing;
    this._toDressing = this._dressingFor(next);
    this._toMode = next;
    this._transitionDuration = duration;
    this._transitionTime = 0;
    this._transitioning = true;
  }

  /**
   * Glide into `mode` from an externally supplied camera pose, over a custom
   * duration. Used for the start-screen → cockpit intro: the "from" pose is the
   * orbit camera's last frame (captured before the scenario swap) rather than
   * the live camera, so the view flows continuously from the parked beauty shot
   * straight into the cockpit.
   */
  introTo(mode, fromPose, duration = this.transitionDuration) {
    // Leaving the start-screen orbit camera for good — release its mouse/wheel
    // listeners so they don't fight the player's input during/after the glide.
    if (this.mode === CameraMode.START) this.startCamera?.disable();
    this._fromPos.copy(fromPose.position);
    this._fromQuat.copy(fromPose.quaternion);
    this._fromFov = fromPose.fov;
    this._anchorFrom(this._transitioning ? this._toMode : this.mode, mode);
    this._fromDressing = this.dressing;
    this._toDressing = this._dressingFor(mode);
    this._toMode = mode;
    this._transitionDuration = duration;
    this._transitionTime = 0;
    this._transitioning = true;
  }

  /**
   * Cut straight to the first-run intro shots (no glide — the cut happens on
   * black). Releases the start-screen orbit's mouse listeners, which would
   * otherwise let a drag re-aim a shot. The intro leaves through `introTo`,
   * like the start screen does.
   */
  playIntro(introCamera) {
    if (this.mode === CameraMode.START) this.startCamera?.disable();
    this.introCamera = introCamera;
    this._transitioning = false;
    this.mode = this._toMode = CameraMode.INTRO;
    this._dressing = this._dressingFor(CameraMode.INTRO);
  }

  /** Toggle between chase and cockpit (Enter key); also exits the enemy orbit
   *  and the action-camera mounts back to the A-10 view the player left. */
  toggle() {
    if (this.mode === CameraMode.ENEMY || this.mode === CameraMode.ACTION) {
      this._startTransition(this._previousMode);
      return;
    }
    const next = this.mode === CameraMode.CHASE ? CameraMode.COCKPIT : CameraMode.CHASE;
    this._startTransition(next);
  }

  /**
   * Switch to the wreck-watching DESTROYED view when the A-10 is destroyed: the
   * chase camera pulls back to CAMERA.chase.destroyedDistance. Forces the view
   * out of the cockpit (or a normal chase) into DESTROYED, but leaves the
   * player's chosen enemy-orbit view alone — though the destroyed-distance flag
   * is still set so a later chase reads as a wreck shot.
   */
  forceDestroyed() {
    this.chaseCamera.destroyed = true;
    const observing = this.mode === CameraMode.ENEMY;
    if (!observing && this.mode !== CameraMode.DESTROYED && this._toMode !== CameraMode.DESTROYED) {
      this._startTransition(CameraMode.DESTROYED);
    }
  }

  /**
   * Forward pointerLock mouse delta to the orbit camera while in enemy mode.
   * Returns true if the input was consumed (caller should not also drive
   * the player's look direction).
   */
  handleMouseLook(dx, dy, sensitivity) {
    if (this.mode !== CameraMode.ENEMY) return false;
    this.enemyCamera?.applyMouseDelta(dx, dy, sensitivity);
    return true;
  }

  /**
   * Forward wheel input to the orbit camera while in enemy mode (zoom). Returns
   * true if consumed, so the caller doesn't also move the throttle.
   */
  handleWheel(deltaY) {
    if (this.mode !== CameraMode.ENEMY && this._toMode !== CameraMode.ENEMY) return false;
    this.enemyCamera?.applyZoom(deltaY);
    return true;
  }

  /**
   * Cycle through enemies (X key). First press transitions into enemy mode
   * focused on the first entity; subsequent presses advance to the next.
   * No-op if there are no enemies.
   */
  cycleEnemy(entities) {
    if (!this.enemyCamera || !entities || entities.length === 0) return;

    if (this.mode === CameraMode.ENEMY || this._toMode === CameraMode.ENEMY) {
      const idx = entities.indexOf(this.enemyCamera.entity);
      const next = entities[(idx + 1) % entities.length];
      // Smooth glide to the new entity rather than snapping
      this.enemyCamera.setEntity(next, false);
    } else {
      if (this.mode === CameraMode.CHASE || this.mode === CameraMode.COCKPIT) {
        this._previousMode = this.mode;
      }
      this.enemyCamera.setEntity(entities[0], true);
      this._startTransition(CameraMode.ENEMY);
    }
  }

  /**
   * Cycle the model's action-camera mounts (C key). The first press glides to
   * the first mount; each one after that advances, wrapping — the mode is left
   * with Enter, which returns to the chase/cockpit view the player came from.
   * No-op if the model carries no mounts, or on a wreck (its cockpit is gone,
   * the same reason `_startTransition` redirects a cockpit request).
   */
  cycleAction() {
    if (!this.actionCamera || this.actionCamera.count === 0) return;
    if (this.chaseCamera.destroyed) return;

    if (this.mode === CameraMode.ACTION || this._toMode === CameraMode.ACTION) {
      this.actionCamera.advance();
      // Re-point the glide at the new mount: its pose is read live, so the only
      // thing to restate is where the camera is coming from.
      this._startTransition(CameraMode.ACTION, CAMERA.action.transitionDuration);
      return;
    }
    if (this.mode === CameraMode.CHASE || this.mode === CameraMode.COCKPIT) {
      this._previousMode = this.mode;
    }
    this.actionCamera.reset();
    this._startTransition(CameraMode.ACTION, CAMERA.action.transitionDuration);
  }

  update(dt) {
    // Always tick all cameras so they stay warm
    this.chaseCamera.update(dt);
    this.cockpitCamera.update(dt);
    this.enemyCamera?.update(dt);
    this.startCamera?.update(dt);
    this.actionCamera?.update(dt);
    this.introCamera?.update(dt);

    if (this._transitioning) {
      this._transitionTime += dt;
      let t = Math.min(this._transitionTime / this._transitionDuration, 1);

      // Smoother-step easing (quintic) — continuous acceleration at both
      // ends, so the long cinematic intro glide eases in/out smoothly instead
      // of reading as linear through the middle.
      t = t * t * t * (t * (t * 6 - 15) + 10);

      // Carry the start pose along with the airframe, so the glide happens in
      // the frame both ends share rather than in the world (see `_anchorFrom`).
      if (this._fromAnchored) {
        this._fromPos.copy(this.chaseCamera.aircraft.position).add(this._fromOffset);
      }

      const dest = this.target;
      this.camera.position.lerpVectors(this._fromPos, dest.position, t);
      this.camera.quaternion.slerpQuaternions(this._fromQuat, dest.quaternion, t);

      // Lerp FOV during transition
      const targetFov = this._fovForMode(this._toMode);
      this.camera.fov = this._fromFov + (targetFov - this._fromFov) * t;
      this.camera.updateProjectionMatrix();

      if (t >= 1) {
        this._transitioning = false;
        this.mode = this._toMode;
        this._dressing = this._toDressing;
      }
    } else {
      // Directly follow the active camera
      const src = this.active;
      this.camera.position.copy(src.position);
      this.camera.quaternion.copy(src.quaternion);

      // Smoothly settle FOV (handles hot-reloads / config changes). An intro
      // shot's lens is part of the shot — it lands with the cut, not after it.
      const targetFov = this._fovForMode(this.mode);
      const fovAlpha = this.mode === CameraMode.INTRO ? 1 : 1 - Math.exp(-(CAMERA.fovSmoothing) * dt);
      this.camera.fov += (targetFov - this.camera.fov) * fovAlpha;
      this.camera.updateProjectionMatrix();
    }
  }

  dispose() {
    this.startCamera?.dispose();
  }
}
